import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Injector,
  OnDestroy,
  OnInit,
  TemplateRef,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTableModule } from 'ng-zorro-antd/table';
import { finalize, Observable, Subject, take, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { ApiResponse } from '../../../../../core/models/api-response.models';
import { ApiResponses } from '../../../../../core/models/common.models';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { CommonService } from '../../../../../shared/services/common.service';
import { CustomQuestionReportType, InsightMode } from '../../../insights.enums';
import {
  Attachment,
  CaseDetailModel,
  EditBehaviorDrawerParams,
} from '../../../insights.interface';
import { FrequencyDatasheetGraphComponent } from '../../insights-data/data-graph/frequency-datasheet-graph/frequency-datasheet-graph.component';
import {
  BehaviorGraphDataResponse,
  CaseBehaviorUnitMapping,
  caseBehaviorUnitMappings,
  frequencyDatasheetGraph,
  frequencyIntervalGraph,
  GraphFilterEvent,
  GraphList,
  latencyGraph,
  partialIntervalGraph,
  replacementDataType,
} from '../../insights-data/insights-data.interface';
import { InsightsDataService } from '../../insights-data/insights-data.service';
import { ManageGraphComponent } from '../../insights-data/manage-graph.component';
import { PrintBipComponent } from '../print-bip/print-bip.component';
import {
  behaviorData,
  BipPrintData,
  CaseAttachment,
  CustomQuestionAnswer,
  PlanTypeAttachment,
} from './print-bip.interface';
import { PrintBipService } from './print-bip.service';
import { BipTemplateService } from '../../../../bip-template/bip-template.service';
import { StudentAttachmentService } from '../../insights-admin/student-attachment.service';
import { TemplateModel } from '../../../../bip-template/bip-template.interface';
import { OrganizationService } from '../../../../organizations/organization.service';
import { OrganizationModel } from '../../../../organizations/organization.interface';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { format, parse } from 'date-fns';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { LoaderService } from '../../../../../core/services/loader.service';
import { forEach, groupBy, map, union } from 'lodash';
import { LatencyGraphComponent } from '../../insights-data/data-graph/latency-graph/latency-graph.component';
import { PartialIntervalGraphComponent } from '../../insights-data/data-graph/partial-interval-graph/partial-interval-graph.component';
import { ReplacementBehaviorGraphComponent } from '../../insights-data/data-graph/replacement-behavior-graph/replacement-behavior-graph.component';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { CustomQuestionService } from '../../../../manage-settings/custom-question/custom-question.service';
import { QuestionItem } from '../../../../manage-settings/manage-settings.interface';
import { FrequencyIntervalGraphComponent } from '../../insights-data/data-graph/frequency-interval-graph/frequency-interval-graph.component';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { FeedbackService } from '../../feedback/feedback.service';
import { FeedbackComponent } from '../../feedback/feedback.component';
import { DrawerHeaderComponent } from '../../../../../components/drawer-header/drawer-header.component';
import { UserDatePipe } from '../../../../../shared/pipes/user-date.pipe';
import { GraphFilterComponent } from '../../insights-data/data-graph/graph-filter/graph-filter.component';
import { DATE_FORMAT } from '../../feedback/feeback.enum';

@Component({
  selector: 'app-print-options',
  standalone: true,
  imports: [
    SvgIconComponent,
    NzCheckboxModule,
    NzFormModule,
    NzFormItemComponent,
    NzCheckboxModule,
    NzDividerModule,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzDatePickerModule,
    NzInputModule,
    NzTableModule,
    NzButtonModule,
    FormsModule,
    ReactiveFormsModule,
    DatePipe,
    CommonModule,
    NzSpinModule,
    NzSelectModule,
    NzModalModule,
    DrawerHeaderComponent,
    UserDatePipe,
    GraphFilterComponent,
  ],
  templateUrl: './print-options.component.html',
  styleUrl: './print-options.component.scss',
})
export class PrintOptionsComponent
  extends ManageGraphComponent
  implements OnInit, OnDestroy
{
  @ViewChild('feedbackDataTpl') feedbackDataTpl!: TemplateRef<void>;
  loading$: Observable<boolean>;
  caseParams: EditBehaviorDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.PRINT_CASE,
  };
  caseData = {} as BipPrintData;
  graphList: GraphList[] = [];
  organizationDetail?: OrganizationModel;
  bipTemplate?: TemplateModel | null;
  PDF_MEDIA_TYPE = 1;
  checkedAttachments: CaseAttachment[] = [];
  checkedGraphList: GraphList[] = [];
  customQuestionList: QuestionItem[] = [];
  datasheetData: { id: number; value: string }[] = [
    { id: 1, value: 'Frequency Based Datasheet' },
    { id: 2, value: 'Latency' },
    { id: 3, value: 'Partial Interval' },
    { id: 4, value: 'Replacement Behavior' },
  ];
  templateSections = [
    { id: 1, sectionName: 'Challenging Behavior' },
    { id: 2, sectionName: 'Strategy Details' },
    { id: 3, sectionName: 'Custom Questions' },
    { id: 4, sectionName: 'Reports' },
  ];
  isPrintLoading = false;
  printOptionsForm!: FormGroup;
  printDraft = false;
  addGraphToReports = false;
  startDate?: Date;
  endDate?: Date;
  isFeedbackSubmitted$: Observable<boolean>;
  isModalVisible = false;
  isAddGraph = false;
  isAddComments = false;
  caseAttachments: PlanTypeAttachment[] = [];
  activePlanAttachments: PlanTypeAttachment[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private dataService: InsightsDataService,
    private drawerService: DrawerService,
    private commonService: CommonService,
    private viewContainerRef: ViewContainerRef,
    private PrintBipService: PrintBipService,
    private templateService: BipTemplateService,
    private studentAttachmentService: StudentAttachmentService,
    private customQuestionService: CustomQuestionService,
    private orgService: OrganizationService,
    private loaderService: LoaderService,
    private injector: Injector,
    private fb: FormBuilder,
    private feedbackService: FeedbackService,
    cd: ChangeDetectorRef
  ) {
    super(cd);
    this.loading$ = this.loaderService.loading$;
    this.isFeedbackSubmitted$ = this.feedbackService.feedbackSubmissionState$;
  }

  ngOnInit(): void {
    this.setupDrawerSubscription();
    this.initForm();
  }

  initForm() {
    this.printOptionsForm = this.fb.group({
      comment: [null],
      isDraft: [false],
      isIncludeStrategyGuidelines: [false],
      frequencyInterval: [false],
      isGraph: [false],
      isComments: [false],
      startDate: [null],
      endDate: [null],
      viewType: [1],
      isTrendLineEnabled: [false],
    });
  }

  setupDrawerSubscription() {
    this.drawerService
      .getParams<EditBehaviorDrawerParams>(DRAWER_IDS.BIP_PRINT_OPTIONS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          this.getMasterData();
        }
      });
  }

  async getMasterData() {
    this.loaderService.show();
    try {
      const [templateData, attchmentRes, orgDetail, queResponse] =
        await Promise.all([
          this.templateService
            .getTemplateById(this.caseParams.case.bipTemplateId)
            .toPromise(),
          this.PrintBipService.getCaseAttachmentsByPlanTypeAndCaseId(
            this.caseParams.case.id
          ).toPromise(),
          this.orgService
            .getOrganization(this.caseParams?.case?.student.organizationId)
            .toPromise(),
          this.customQuestionService
            .getQuestionsByOrg(this.caseParams?.case?.student.organizationId)
            .toPromise(),
        ]);
      this.caseAttachments = attchmentRes?.result?.data || [];
      this.bipTemplate = templateData ? templateData?.result : null;
      this.activePlanAttachments = this.caseAttachments
        .map(attachment => {
          if (attachment.planTypeId === 0) {
            return { ...attachment, sequenceNumber: 0 };
          }
          if (this.bipTemplate) {
            this.printOptionsForm.patchValue({
              isIncludeStrategyGuidelines:
                this.bipTemplate.isIncludeStrategyGuidelines,
            });
          }
          const matchingSection = this.bipTemplate?.templateSections?.find(
            section =>
              section.sectionName === attachment.planTypeName &&
              section.isSelected === true
          );
          return matchingSection
            ? { ...attachment, sequenceNumber: matchingSection.sequenceNumber }
            : null;
        })
        .filter(attachment => attachment !== null)
        .sort((a, b) => a.sequenceNumber - b.sequenceNumber);
      this.organizationDetail = orgDetail;
      this.customQuestionList = queResponse ? queResponse?.result : [];
      this.getBehaviorReportDetails();
    } catch (error) {
      console.error('Error loading master data:', error);
      this.commonService.notify('error', 'Failed to load data');
      this.loaderService.hide();
    }
  }

  onFilterChange(event: GraphFilterEvent) {
    this.printOptionsForm.patchValue({
      viewType: event.viewType,
      isTrendLineEnabled: event.isTrendLineEnabled,
      startDate: parse(event.startDate, DATE_FORMAT, new Date()),
      endDate: parse(event.endDate, DATE_FORMAT, new Date()),
    });
    this.validateDates('startDate', 'endDate');
  }

  validateDates(ctrl1: string, ctrl2: string) {
    const startDate = this.printOptionsForm.get(ctrl1)?.value;
    const endDate = this.printOptionsForm.get(ctrl2)?.value;

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start > end) {
        this.printOptionsForm.get(ctrl1)?.setErrors({ dateInvalid: true });
        this.printOptionsForm.get(ctrl2)?.setErrors({ dateInvalid: true });
      } else {
        this.printOptionsForm.get(ctrl1)?.setErrors(null);
        this.printOptionsForm.get(ctrl2)?.setErrors(null);
      }
    }

    this.printOptionsForm.get('isGraph')?.setValue(false);
    this.printOptionsForm.get('isComments')?.setValue(false);
    this.resetGraphs();
  }

  getBehaviorReportDetails() {
    this.loaderService.show();
    this.PrintBipService.getPrintBipData(this.caseParams.case.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ApiResponse<BipPrintData>) => {
          if (res.result) {
            this.caseData = res.result;
            this.caseData.caseBehaviors.forEach(behavior => {
              behavior['caseBehaviorTypeIds'] = behavior.caseBehaviorTypes.map(
                t => t.behaviorTypeName
              );
              behavior['caseBehaviorTypeDescriptionIds'] =
                behavior.caseBehaviorTypeDescriptions.map(t => t.description);
              behavior['caseBehaviorEnvironmentIds'] =
                behavior.caseBehaviorEnvironments.map(
                  t => t.behaviorEnvironmentName
                );
              behavior['caseBehaviorSettingEventIds'] =
                behavior.caseBehaviorSettingEvents.map(
                  t => t.behaviorSettingEventName
                );
              behavior['caseBehaviorAntecedentIds'] =
                behavior.caseBehaviorAntecedents.map(
                  t => t.behaviorAntecedentName
                );
              behavior['caseBehaviorConsequenceIds'] =
                behavior.caseBehaviorConsequences.map(
                  t => t.behaviorConsequenceName
                );
              behavior['caseBehaviorConsequenceDescriptionIds'] =
                behavior.caseBehaviorConsequenceDescriptions.map(
                  t => t.description
                );

              if (this.caseData.bipTemplateId === 1) {
                behavior.caseBehvaiorCustomQuestionIds = this.customQuestionList
                  .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
                  .filter(
                    question =>
                      question.statusId === 1 &&
                      question.questionForReportTypeId !==
                        CustomQuestionReportType.Details
                  )
                  .map(question => {
                    const matchedAnswer =
                      behavior.caseBehaviorCustomQuestionAnswer.find(
                        answer => answer.customQuestionId === question.id
                      );
                    return {
                      ...question,
                      answer: matchedAnswer ? matchedAnswer.answer : '',
                    };
                  });
              } else {
                const resultList = this.bipTemplate?.templateQuestionIds
                  .filter(
                    id =>
                      !this.bipTemplate?.behaviorQuestionaries.some(
                        q => q.questionId === id
                      )
                  )
                  .map((id: number) => {
                    const matchedQuestion = this.customQuestionList.find(
                      q =>
                        q.id === id &&
                        q.statusId === 1 &&
                        q.questionForReportTypeId !==
                          CustomQuestionReportType.Details
                    );

                    if (!matchedQuestion) return null;

                    const matchedAnswer =
                      behavior.caseBehaviorCustomQuestionAnswer.find(
                        q => q.customQuestionId === id
                      );

                    return {
                      id,
                      customQuestion: matchedQuestion.customQuestion,
                      answer: matchedAnswer ? matchedAnswer.answer : '',
                    };
                  })
                  .filter((q): q is CustomQuestionAnswer => q !== null);

                behavior['caseBehvaiorCustomQuestionIds'] = resultList || [];
              }
            });
            this.caseData.bipTemplateChallengingBehaviors
              .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
              .forEach(behavior => {
                if (Array.isArray(behavior.questionIds)) {
                  behavior.questionIds.sort(
                    (a, b) => a.sequenceNumber - b.sequenceNumber
                  );
                }
              });
            this.cd.detectChanges();
          }
          this.loaderService.hide();
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
          this.loaderService.hide();
        },
      });
  }

  getGraphs() {
    this.loaderService.show();
    this.dataService
      .getGraphList(this.caseParams.case.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: (res: ApiResponses<GraphList>) => {
          if (res.result) {
            this.graphList = res.result;
            this.checkedGraphList = this.graphList;
            this.cd.detectChanges();
          }
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  onOptionsCheckboxChange(event: boolean) {
    if (event) {
      this.isAddGraph = true;
      this.getGraphs();
    } else {
      this.graphList = [];
      this.resetGraphs();
      this.isAddGraph = false;
    }
  }

  onCommentCheckboxChange(event: boolean) {
    this.isAddComments = event;
  }

  resetGraphs() {
    this.checkedGraphList = [];
    this.graphList = [];
    forEach(this.caseData.caseBehaviors, behavior => {
      behavior['graphImages'] = [];
      behavior['comments'] = [];
    });
  }

  onGraphCheckboxChange(event: boolean, data: GraphList) {
    forEach(this.caseData.caseBehaviors, behavior => {
      behavior['graphImages'] = [];
      behavior['comments'] = [];
    });
    if (event) {
      this.checkedGraphList.push(data);
    } else {
      const index = this.checkedGraphList.findIndex(
        item => item.id === data.id
      );
      this.checkedGraphList.splice(index, 1);
      this.displayNoData = true;
      this.cd.detectChanges();
    }
  }

  onAttachmentCheckboxChange(event: boolean, data: CaseAttachment) {
    if (event) {
      if (data.fileUrl !== '') {
        this.checkedAttachments.push(data);
      }
    } else {
      const index = this.checkedAttachments.findIndex(
        item => item.id === data.id
      );
      if (index !== -1) {
        this.checkedAttachments.splice(index, 1);
      }
    }
  }

  isPdfFile(fileName: string): boolean {
    if (!fileName) return false;
    return fileName.toLowerCase().endsWith('.pdf');
  }

  getFilteredPdfAttachments(attachments: CaseAttachment[]): CaseAttachment[] {
    if (!attachments || !Array.isArray(attachments)) {
      return [];
    }
    return attachments.filter(
      attachment =>
        attachment.fileUrl !== '' &&
        (this.isPdfFile(attachment.name) ||
          attachment.mediaTypeId === this.PDF_MEDIA_TYPE)
    );
  }

  getGraphData() {
    const caseBehaviorUnitMappings: caseBehaviorUnitMappings[] = [];
    const startDate = this.printOptionsForm.get('startDate')?.value;
    const endDate = this.printOptionsForm.get('endDate')?.value;
    forEach(this.caseData.caseBehaviors, behavior => {
      behavior['graphImages'] = [];
      behavior['comments'] = [];
    });
    this.checkedGraphList.forEach(graph => {
      const selectedUnit = this.datasheetData.find(
        d => d.value === graph.unitName
      );
      caseBehaviorUnitMappings.push({
        caseBehaviorId: graph.caseBehaviorId,
        unitName: selectedUnit?.id || 0,
        graphType: selectedUnit?.id === 1 ? 1 : 0,
      });
      if (
        selectedUnit?.id === 1 &&
        this.printOptionsForm.get('frequencyInterval')?.value === true
      ) {
        caseBehaviorUnitMappings.push({
          caseBehaviorId: graph.caseBehaviorId,
          unitName: selectedUnit?.id || 0,
          graphType: 2,
        });
      }
    });
    if (caseBehaviorUnitMappings.length > 0) {
      this.loaderService.show();
      this.dataService
        .getMultipleGraphData({
          caseBehaviorUnitMappings,
          startDate: format(startDate, DATE_FORMAT),
          endDate: format(endDate, DATE_FORMAT),
          viewType: this.printOptionsForm.get('viewType')?.value || 1,
        })
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: async res => {
            if (res.result && res.result.caseBehaviorUnitMappings.length > 0) {
              await this.prepareGraphResponseData(
                res.result.caseBehaviorUnitMappings
              );

              await this.printReport();
            }
            this.loaderService.hide();
          },
          error: (error: Error) => {
            this.loaderService.hide();
            this.commonService.notify('error', error.message);
          },
        });
    } else {
      this.printReport();
    }
  }

  async prepareGraphResponseData(
    caseBehaviorUnitMapping: CaseBehaviorUnitMapping[]
  ): Promise<void> {
    const promises = caseBehaviorUnitMapping.map(element => {
      return new Promise<void>(resolve => {
        if (!element) {
          this.displayNoData = true;
          return resolve();
        }

        const selectedUnit = this.datasheetData.find(
          d => d.value === element.unitName
        );

        if (!selectedUnit) {
          this.displayNoData = true;
          return resolve();
        }

        this.manageGraphResponseData(element, selectedUnit).then(() =>
          resolve()
        );
      });
    });

    await Promise.all(promises);
  }

  async manageGraphResponseData(
    caseBehaviorUnitMapping: CaseBehaviorUnitMapping,
    selectedUnit: { id: number; value: string }
  ): Promise<void> {
    try {
      const caseBehaviorId = caseBehaviorUnitMapping.caseBehaviorId;
      const graphType = caseBehaviorUnitMapping.graphType;
      const data = caseBehaviorUnitMapping.graph;
      const mappedData: BehaviorGraphDataResponse = {
        caseBehaviorId: caseBehaviorId,
        unitName: selectedUnit.value,
        partialInterval:
          selectedUnit.id === 3 ? (data as partialIntervalGraph) : null,
        frequencyDuration:
          selectedUnit.id === 1 && graphType === 1
            ? (data as frequencyDatasheetGraph)
            : null,
        frequencyInterval:
          selectedUnit.id === 1 && graphType === 2
            ? (data as frequencyIntervalGraph)
            : null,
        latency: selectedUnit.id === 2 ? (data as latencyGraph) : null,
        replacementBehavior:
          selectedUnit.id === 4 ? (data as replacementDataType) : null,
      };

      const success = await this.processBehaviorGraphData(
        mappedData,
        selectedUnit.id || 0,
        graphType,
        this.printOptionsForm.get('viewType')?.value || 1,
        this.printOptionsForm.get('isTrendLineEnabled')?.value || false
      );

      if (!success) {
        this.isGraphDataAvailable = false;
        this.displayNoData = true;
      } else {
        const graphData =
          selectedUnit.id === 1
            ? graphType === 2
              ? this.frequencyIntervalGraphData
              : this.frequencyDatasheetGraphData
            : selectedUnit.id === 2
              ? this.latencyGraphData
              : selectedUnit.id === 3
                ? this.partialIntervalGraphData
                : selectedUnit.id === 4
                  ? this.replacementGraphData
                  : null;

        if (!graphData) {
          this.loaderService.hide();
          this.commonService.notify('error', 'Invalid graph data');
          return;
        }

        await this.generateGraphImage(
          selectedUnit.id || 0,
          caseBehaviorUnitMapping.id,
          graphData,
          graphType === 2
        );
      }
      const caseBehavior = this.caseData.caseBehaviors.find(
        b => b.id === caseBehaviorId
      );
      if (
        caseBehavior &&
        data &&
        data.comments &&
        data.comments.length > 0 &&
        this.isAddComments &&
        graphType !== 2
      ) {
        data.comments.forEach(comment => {
          comment.dataUnitId = caseBehaviorUnitMapping.id;
        });
        caseBehavior['comments'] = [
          ...(caseBehavior['comments'] || []),
          ...data.comments,
        ];
      }
    } catch (error) {
      this.loaderService.hide();
      console.error('Error in graph data processing:', error);
      this.commonService.notify('error', 'Error processing graph data');
    }
  }

  async generateGraphImage(
    selectedUnit: number,
    selectedGraph: number,
    data:
      | frequencyDatasheetGraph
      | latencyGraph
      | partialIntervalGraph
      | replacementDataType
      | frequencyIntervalGraph,
    showIntervalFrequency = false
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      type GraphComponent =
        | FrequencyIntervalGraphComponent
        | FrequencyDatasheetGraphComponent
        | LatencyGraphComponent
        | PartialIntervalGraphComponent
        | ReplacementBehaviorGraphComponent;

      type GraphComponentType = Type<GraphComponent>;

      let componentFactory: GraphComponentType;

      switch (selectedUnit) {
        case 1:
          componentFactory = (
            showIntervalFrequency
              ? FrequencyIntervalGraphComponent
              : FrequencyDatasheetGraphComponent
          ) as GraphComponentType;
          break;
        case 2:
          componentFactory = LatencyGraphComponent as GraphComponentType;
          break;
        case 3:
          componentFactory =
            PartialIntervalGraphComponent as GraphComponentType;
          break;
        case 4:
          componentFactory =
            ReplacementBehaviorGraphComponent as GraphComponentType;
          break;
        default:
          resolve();
          return;
      }

      try {
        const factory = this.viewContainerRef.createComponent(
          componentFactory,
          {
            injector: this.injector,
          }
        );

        const componentElement = factory.location.nativeElement;
        componentElement.style.display = 'none';
        factory.instance.graphData = data;

        const caseBehavior = this.caseData.caseBehaviors.find(
          b => b.id === data.caseBehaviorId
        );

        let completed = 0;
        const complete = () => {
          completed++;
          if (
            (selectedUnit === 3 && completed >= 2) ||
            (selectedUnit !== 3 && completed >= 1)
          ) {
            factory.destroy();
            resolve();
          }
        };

        const handleError = (error: unknown): void => {
          this.loaderService.hide();
          console.error('Error in graph image generation:', error);
          factory.destroy();
          reject(error);
        };
        factory.instance.allChartImage
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (images: string[]) => {
              if (caseBehavior && images.length > 0) {
                const graphImages = images.map(img => ({
                  graph: img,
                  unitId: selectedGraph,
                }));
                caseBehavior['graphImages'] = [
                  ...(caseBehavior['graphImages'] || []),
                  ...graphImages,
                ];
              }

              complete();
            },
            error: handleError,
          });

        if (selectedUnit === 3) {
          (factory.instance as PartialIntervalGraphComponent).allChartTotalImage
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (images: string[]) => {
                if (caseBehavior && images.length > 0) {
                  const graphImages = images.map(img => ({
                    graph: img,
                    unitId: selectedGraph,
                  }));
                  caseBehavior['graphImages'] = [
                    ...(caseBehavior['graphImages'] || []),
                    ...graphImages,
                  ];
                }
                complete();
              },
              error: handleError,
            });
        }
      } catch (error) {
        console.error('Error creating graph component:', error);
        this.loaderService.hide();
        reject(error);
      }
    });
  }

  closeDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.BIP_PRINT_OPTIONS);
  }

  onCancel() {
    this.checkedAttachments = [];
    this.checkedGraphList = [];
    this.printOptionsForm.reset();
    this.closeDrawer();
  }

  groupGraphsAndComments(behavior: behaviorData) {
    const graphGroups = groupBy(behavior.graphImages, g => g.unitId.toString());
    const commentGroups = groupBy(behavior.comments, c =>
      c.dataUnitId.toString()
    );
    const allUnitIds = union(
      Object.keys(graphGroups),
      Object.keys(commentGroups)
    );
    return map(allUnitIds, unitIdStr => {
      const unitId = Number(unitIdStr);
      const commentsByDate = groupBy(
        commentGroups[unitIdStr] || [],
        c => c.dataDate
      );
      const commentsData = map(
        Object.keys(commentsByDate),
        (dateKey, index) => ({
          dataDate: dateKey,
          id: index,
          comments: commentsByDate[dateKey].map(c => ({
            id: c.commentId,
            comment: c.commentText,
            createdBy: c.createdBy,
            createdOn: c.createdOn,
            createdByName: c.createdByName,
          })),
        })
      );
      return {
        unitId: Number(unitId),
        graphs: map(graphGroups[unitIdStr] || [], g => ({
          graph: g.graph,
          unitId: g.unitId,
        })),
        commentsData: commentsData,
      };
    });
  }

  async printReport() {
    const factory = this.viewContainerRef.createComponent(PrintBipComponent);
    const componentElement = factory.location.nativeElement;
    componentElement.style.display = 'none';
    this.loaderService.show();

    try {
      const studentAge = this.caseData.student?.dateOfBirth
        ? this.PrintBipService.calculateAge(this.caseData.student.dateOfBirth)
        : null;
      this.caseData.age = studentAge;

      if (this.bipTemplate?.templateSections) {
        this.bipTemplate.templateSections =
          this.bipTemplate.templateSections.filter(section => {
            const matchingSection = this.templateSections.find(
              templateSection =>
                templateSection.sectionName === section.sectionName
            );
            if (matchingSection) {
              section.id = matchingSection.id;
              return section.isSelected !== false;
            }
            return false;
          });
      }

      if (this.checkedGraphList.length > 0) {
        for (const element of this.caseData.caseBehaviors) {
          const res = this.groupGraphsAndComments(element);
          element['groupedGraphsAndComments'] = res;
        }
      }

      this.checkedAttachments = this.checkedAttachments.sort((a, b) => {
        const attachmentA = this.activePlanAttachments.find(
          planAttachment => planAttachment.planTypeId === a.planTypeId
        );
        const attachmentB = this.activePlanAttachments.find(
          planAttachment => planAttachment.planTypeId === b.planTypeId
        );

        const sequenceA = attachmentA?.sequenceNumber ?? -1;
        const sequenceB = attachmentB?.sequenceNumber ?? -1;

        if (sequenceA === -1 && sequenceB !== -1) return -1;
        if (sequenceA !== -1 && sequenceB === -1) return 1;

        return sequenceA - sequenceB;
      });

      factory.instance.printOptions = {
        organizationDetail: this.organizationDetail,
        caseData: this.caseData,
        bipTemplate: this.bipTemplate,
        logo: null,
        graphData: null,
        attachments: this.checkedAttachments.map(
          (attachment: Attachment) => attachment.fileUrl
        ),
        PrintOptions: this.printOptionsForm.value,
      };

      this.cd.detectChanges();

      await this.waitForInitialization(factory);
      await factory.instance.printPDF();
      factory.destroy();
      this.loaderService.hide();
      this.onCancel();
    } catch (error) {
      factory.destroy();
      this.loaderService.hide();
      this.commonService.notify('error', 'Error generating PDF');
      console.error('Error generating PDF:', error);
    }
  }

  waitForInitialization(factory: ComponentRef<PrintBipComponent>) {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (factory.instance.initialized) {
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }

  handleModalCancel(): void {
    this.onCancel();
    this.isModalVisible = false;
  }

  handleModalConfirm(): void {
    this.drawerService.openDrawer(
      FeedbackComponent,
      this.feedbackDataTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        student: this.caseParams?.case.student,
        supervisorName: this.caseParams.case?.supervisorName,
        caseId: this.caseParams.case?.id,
      },
      DRAWER_IDS.CASE_FEEDBACK
    );
    this.isModalVisible = false;
  }

  closefeedbackFormDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.CASE_FEEDBACK);
    this.isModalVisible = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
